import { BaseAction, State } from './index'
import {
    CampaignRequest,
    GetCampaignCommentsRequest,
    PatchCommentRequest,
    DeleteCommentRequest,
    ResolveThreadRequest,
} from 'services/api/campaigns'
import { createSelector } from 'reselect'

import _ from 'lodash'

export enum CommentActions {
    GET_CAMPAIGN_COMMENTS = 'Comment/GET_CAMPAIGN_COMMENTS',
    SAVE_CAMPAIGN_COMMENTS = 'Comment/SAVE_CAMPAIGN_COMMENTS',
    ADD_CAMPAIGN_COMMENTS = 'Comment/ADD_CAMPAIGN_COMMENTS',
    UPDATE_CAMPAIGN_COMMENT = 'Comment/UPDATE_CAMPAIGN_COMMENT',
    UPDATE_CURRENT_IDENTIFIER = 'Comment/UPDATE_CURRENT_IDENTIFIER',
    REPLY_CAMPAIGN_COMMENT = 'Comment/REPLY_CAMPAIGN_COMMENT',
    DELETE_CAMPAIGN_COMMENT = 'Comment/DELETE_CAMPAIGN_COMMENT',
    RESOLVE_CAMPAIGN_THREAD = 'RESOLVE_CAMPAIGN_THREAD',
}

export interface CommentState {
    uuid: string
    comment: string
    cprofile: {
        uuid: string
        name: string
        team_name: string
        is_active: boolean
    }
    created_at: string
    updated_at: string
    edited_at: null | string
}

export interface ThreadCommentState {
    uuid: string
    comments: CommentState[]
    resolved_at: null | string
}

export interface FieldCommentState {
    field_identifier: string
    threads: ThreadCommentState[]
}

export type CampaignCommentState = {
    currentField: null | string
    comments: Record<string, Record<string, ThreadCommentState[]>>
}

export const getCampaignFieldsComments = (payload: GetCampaignCommentsRequest) => {
    return {
        payload,
        type: CommentActions.GET_CAMPAIGN_COMMENTS,
    }
}
interface SaveCampaignCommentsPayload extends CampaignRequest {
    comments?: FieldCommentState[]
}
export const saveCampaignComments = (payload: SaveCampaignCommentsPayload) => ({
    payload,
    type: CommentActions.SAVE_CAMPAIGN_COMMENTS,
})
export interface AddCommentRequest extends CampaignRequest {
    field_identifier: string
    comment: string
    onSuccessCallback: () => void
    onErrorCallback: () => void
}

export interface ReplyCommentRequest extends AddCommentRequest {
    commentUuid: string
}

export const addComment = (payload: AddCommentRequest) => ({
    payload,
    type: CommentActions.ADD_CAMPAIGN_COMMENTS,
})

export const replyComment = (payload: ReplyCommentRequest) => ({
    payload,
    type: CommentActions.REPLY_CAMPAIGN_COMMENT,
})

export const updateCurrentField = (payload: { identifier: string | null }) => ({
    payload,
    type: CommentActions.UPDATE_CURRENT_IDENTIFIER,
})

export const updateComment = (payload: PatchCommentRequest) => ({
    payload,
    type: CommentActions.UPDATE_CAMPAIGN_COMMENT,
})

export const deleteComment = (payload: DeleteCommentRequest) => ({
    payload,
    type: CommentActions.DELETE_CAMPAIGN_COMMENT,
})

export const resolveThread = (payload: ResolveThreadRequest) => ({
    payload,
    type: CommentActions.RESOLVE_CAMPAIGN_THREAD,
})

const initialState: CampaignCommentState = {
    currentField: null,
    comments: {},
}

export const commentReducer = (state: CampaignCommentState = initialState, action: BaseAction) => {
    switch (action.type) {
        case CommentActions.SAVE_CAMPAIGN_COMMENTS: {
            const { uuid, comments } = action.payload
            if (uuid && comments.length > 0) {
                const transformedData = comments.reduce(
                    (result: Record<string, ThreadCommentState[]>, next: FieldCommentState) => {
                        result[next.field_identifier] = next.threads
                        return result
                    },
                    {} as Record<string, CommentState[]>,
                )
                return { ...state, comments: { ...comments, [uuid]: transformedData } }
            } else {
                return state
            }
        }
        case CommentActions.UPDATE_CURRENT_IDENTIFIER: {
            const { identifier } = action.payload
            return { ...state, currentField: identifier }
        }
        case CommentActions.RESOLVE_CAMPAIGN_THREAD: {
            const { identifier, resolved, uuid, threadId } = action.payload
            const campaignThreads = _.cloneDeep(state.comments[uuid])
            if (campaignThreads && campaignThreads[identifier]) {
                campaignThreads[identifier] = campaignThreads[identifier].map(thread => {
                    if (thread.uuid === threadId) {
                        thread.resolved_at = resolved ? 'mock' : null
                    }
                    return thread
                })
            }
            return { ...state, comments: { ...state.comments, [uuid]: campaignThreads } }
        }
        default:
            return state
    }
}

export const selectCampaignComments = (campaignUuid?: string | null) => (state: State) => {
    if (campaignUuid) {
        return state.comment.comments[campaignUuid]
    } else {
        return null
    }
}

export const selectCommentsByIdentifier = (campaignUuid?: string, fieldIdentifier?: string) =>
    createSelector(selectCampaignComments(campaignUuid), campaignComments => {
        if (campaignComments && fieldIdentifier) {
            return campaignComments[fieldIdentifier]
        } else {
            return null
        }
    })

export const selectCurrentField = (state: State) => {
    return state.comment.currentField
}
